import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ArrowRightIcon,
  CheckIcon,
  MinusIcon,
  PlusIcon,
  V2Button,
  V2Heading,
  V2Tooltip,
  XIcon,
} from "@stacklet/ui";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";

import { Avatar } from "app/components/avatar/Avatar";
import { ExportCsv } from "app/components/csv-export/ExportCsv";
import { HumanTimestamp } from "app/components/HumanTimestamp";
import { Link } from "app/components/links";
import { StackletGrid } from "app/components/table/StackletGrid";
import { SEARCH_DEBOUNCE_MS } from "app/constants";
import { useCursorPagination } from "app/hooks/grid";
import { createMultipleFilter, createSingleFilter } from "app/utils/filters";

import type { UserBrowser_users$key } from "./__generated__/UserBrowser_users.graphql";
import type { UserBrowserQuery } from "./__generated__/UserBrowserQuery.graphql";
import type { UserBrowserRefetchQuery } from "./__generated__/UserBrowserRefetchQuery.graphql";
import type { GridColDef } from "@mui/x-data-grid-pro";
import type { CursorVariables } from "app/hooks/grid";
import type { ExportConnectionInput } from "app/mutations/__generated__/ExportConnectionMutation.graphql";
import type { FilterElementInput } from "app/utils/filters";

/** Selectable page sizes for the user browser */
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100] as const;
/** Default number of users per page */
const DEFAULT_PAGE_SIZE = 5;
/** Width of the add/remove column, sized to the icon button it holds */
const TRANSFER_COLUMN_WIDTH = 44;
/** Fields the grid must leave at their declared width. Stable identity. */
const FIXED_WIDTH_FIELDS = ["transfer"];

/**
 * Every field the browser can render, for {@link Props.fixedColumnWidths}.
 * Exempting a field that isn't currently rendered is harmless, so this needn't
 * track which optional columns are on. Stable identity.
 */
const ALL_FIELDS = [
  "transfer",
  "name",
  "isSsoUser",
  "hasApiKeys",
  "lastLogin",
  "actions",
];

/** Columns included in the CSV export of the listed users */
const CSV_COLUMNS = [
  { name: "Name", path: "name" },
  { name: "Email", path: "email" },
  { name: "Last Login", path: "lastLogin" },
] as const;

// Match the design's grid chrome: space-xs cell padding, a short header row with
// small secondary-coloured labels, and no vertical padding fighting the fixed
// row height.
const GRID_CLASS_NAMES = [
  "[&_.MuiDataGrid-cell]:!px-xs",
  "[&_.MuiDataGrid-columnHeader]:!px-xs",
  "[&_.MuiDataGrid-columnHeader]:!text-text-secondary",
  "[&_.MuiDataGrid-columnHeaderTitle]:!text-label-small",
].join(" ");

/**
 * Which side of a group's membership a group-scoped browser lists: the group's
 * direct members, or everyone who isn't one.
 */
export type GroupMembership = "members" | "non-members";

/**
 * Which slice of the user directory a browser lists: one side of a group's
 * membership, or every user provisioned by an identity provider.
 */
export type UserBrowserScope =
  | {
      kind: "group";
      /** Name of the group whose membership defines the list */
      groupName: string;
      /** Which side of the membership; defaults to the group's members */
      membership?: GroupMembership;
    }
  | {
      kind: "identity-provider";
      /** The `idpNamespace` of the provider whose users define the list */
      idpNamespace: string;
    };

/** A user as handed to {@link Props.onTransfer} */
export interface TransferUser {
  /** Relay node id, for invalidating the user's cached record */
  id: string;
  /** Stable identifier, used to add/remove the user as a group member */
  uuid: string;
  /** Human label for alerts and accessible names */
  label: string;
}

/**
 * Negates a filter. `createMultipleFilter` collapses a single-operand group to
 * that operand, which would silently drop the negation, so build it directly.
 */
function negate(
  filter: FilterElementInput | undefined,
): FilterElementInput | undefined {
  return filter
    ? { multiple: { operator: "NOT", operands: [filter] } }
    : filter;
}

/**
 * Builds the filter for one scope of the user directory, optionally narrowed by
 * a search term matched against the username.
 *
 * Group membership means direct membership of the named group — the
 * `sso-group` filter resolves through the group-member table, so it covers
 * locally-managed groups too. A provider's namespace must match exactly:
 * `idp-namespace` is a text-search filter whose default operator is
 * `contains`, which would leak users from any provider whose namespace merely
 * contains this one (`saml::okta` vs `saml::okta-dev`).
 */
export function buildFilter(
  scope: UserBrowserScope,
  search: string,
): FilterElementInput | undefined {
  const term = search.trim();
  const searchOperand = term
    ? createSingleFilter("username", term, "contains")
    : undefined;

  if (scope.kind === "identity-provider") {
    return createMultipleFilter([
      createSingleFilter("idp-namespace", scope.idpNamespace, "equals"),
      searchOperand,
    ]);
  }

  const isMember = createSingleFilter(
    "sso-group",
    scope.groupName,
    "array_contains",
  );
  return createMultipleFilter([
    scope.membership === "non-members" ? negate(isMember) : isMember,
    searchOperand,
  ]);
}

/** A single row in the user grid */
interface UserRow {
  id: string;
  uuid: string;
  key: number;
  name?: string | null;
  email?: string | null;
  lastLogin?: string | null;
  isSsoUser: boolean;
  hasApiKeys: boolean;
}

/** Props for the UserBrowser component */
interface Props {
  /** Which users to list; see {@link UserBrowserScope} */
  scope: UserBrowserScope;
  /** Heading above the grid; pass null to render none */
  title?: string | null;
  /**
   * Explanatory line under the heading. Defaults to none: what the list means
   * depends on how its scope is managed, which only the caller knows.
   */
  description?: string | null;
  /** Whether to offer a CSV export of the (filtered) list */
  enableExport?: boolean;
  /**
   * Whether to show the SSO origin column. Defaults on for a group, which can
   * mix SSO-provisioned and locally-created users; an identity provider's
   * users all arrive via SSO, so the column would say nothing there.
   */
  showSsoOrigin?: boolean;
  /**
   * Whether to hold every column at its declared width instead of autosizing
   * from content (the name column still flexes to fill the remainder). Set
   * when several browsers render side by side — e.g. one per expanded row —
   * so their columns line up and don't visibly resize after each mounts.
   */
  fixedColumnWidths?: boolean;
  /**
   * Search term supplied by the parent. Providing it hands search over to the
   * caller: the grid's own toolbar (and its search field) is hidden and the
   * term is applied as given, so the caller owns any debouncing.
   */
  searchTerm?: string;
  /** Storage key for the grid's sticky column state; unique per rendered grid */
  tableId?: string;
  /**
   * Row action that moves the user in or out of the group. Supplying it adds a
   * leading column of `+` (for a non-member) or `−` (for a member) buttons.
   * Only meaningful for a group scope, whose membership can be edited.
   */
  onTransfer?: (user: TransferUser) => void;
  /** Disables the transfer buttons, e.g. while a membership change is in flight */
  isTransferDisabled?: boolean;
  /** Tooltip explaining why the transfer buttons are disabled */
  transferDisabledReason?: string;
  /**
   * Changing this value refetches the first page. Used to resync the list when
   * something outside it — a membership change made on the other list —
   * invalidates what it is showing.
   */
  refreshToken?: number;
}

/**
 * Browses one scope of the user directory: a searchable, sortable,
 * cursor-paginated grid showing an avatar, name and email, whether they hold
 * API keys and their last login, with a link through to each user's details
 * page and CSV export of the (filtered) list.
 *
 * The scope picks the slice: one side of a group's membership (the read-only
 * members list on the group details page, or half of a transfer list), or the
 * users an identity provider has provisioned (the browser on its details
 * page). By default the browser brings its own search field, heading and
 * export; `searchTerm` hands search to a parent driving several grids from one
 * field, and `onTransfer` adds the add/remove column of a transfer list. See
 * {@link GroupMembershipTransferList}.
 *
 * Sorting, column controls and the toolbar come from the shared XGridTable
 * (MUI DataGrid). Pagination replaces the page on each navigation (rather than
 * appending), so it uses `useRefetchableFragment` with explicit forward
 * (`first`/`after`) and backward (`last`/`before`) cursors instead of a
 * `@connection`-backed pagination fragment.
 */
export function UserBrowser({
  scope,
  title = "Users",
  description = null,
  enableExport = true,
  showSsoOrigin = scope.kind === "group",
  fixedColumnWidths = false,
  searchTerm,
  tableId = "user-browser",
  onTransfer,
  isTransferDisabled = false,
  transferDisabledReason,
  refreshToken,
}: Props) {
  // With a `searchTerm` the parent owns the term (and its debounce); otherwise
  // the browser renders its own field and debounces internally.
  const isSearchControlled = searchTerm !== undefined;

  // `search` is the live input value; `appliedSearchRef` holds the term the
  // currently loaded page (and its cursors) was fetched with. Pagination builds
  // its filter from the ref so a not-yet-debounced keystroke can't pair a new
  // filter with a cursor from the old result set — and reading a ref (rather
  // than state) keeps the navigation callbacks stable.
  const [search, setSearch] = useState(searchTerm ?? "");
  const appliedSearchRef = useRef(searchTerm ?? "");
  const [exportInProgress, setExportInProgress] = useState(false);
  const [exportParams, setExportParams] =
    useState<ExportConnectionInput["params"]>(undefined);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // The scope is read through this ref at navigation time so an inline scope
  // object — a fresh identity every render — can't destabilise the navigation
  // callbacks the way depending on it would.
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  // Clear any pending search debounce on unmount so a timer scheduled just
  // before navigating away can't fire a refetch (and state update) on the
  // unmounted component or retain its closure until the timer elapses.
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Read the query variables once: every later change (search, refresh, paging)
  // goes through `refetch`, which keeps the loaded page mounted rather than
  // re-suspending the whole browser.
  const initialVariablesRef = useRef({
    filterElement: buildFilter(scope, searchTerm ?? ""),
    first: DEFAULT_PAGE_SIZE,
  });

  const queryData = useLazyLoadQuery<UserBrowserQuery>(
    graphql`
      query UserBrowserQuery($filterElement: FilterElementInput, $first: Int) {
        ...UserBrowser_users
          @arguments(filterElement: $filterElement, first: $first)
      }
    `,
    initialVariablesRef.current,
  );

  const [data, refetch] = useRefetchableFragment<
    UserBrowserRefetchQuery,
    UserBrowser_users$key
  >(
    graphql`
      fragment UserBrowser_users on Query
      @argumentDefinitions(
        first: { type: "Int" }
        last: { type: "Int" }
        after: { type: "String" }
        before: { type: "String" }
        filterElement: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "UserBrowserRefetchQuery") {
        users(
          first: $first
          last: $last
          after: $after
          before: $before
          filterElement: $filterElement
        ) {
          edges {
            node {
              id
              uuid
              key
              name
              email
              lastLogin
              ssoUser
              apiKeys {
                pageInfo {
                  total
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
            total
          }
        }
      }
    `,
    queryData,
  );

  const rows: UserRow[] = (data.users.edges ?? [])
    .map((edge) => edge.node)
    .filter((node): node is NonNullable<typeof node> => Boolean(node))
    .map((node) => ({
      id: node.id,
      uuid: node.uuid,
      key: node.key,
      name: node.name,
      email: node.email,
      lastLogin: node.lastLogin,
      isSsoUser: node.ssoUser,
      hasApiKeys: Boolean(node.apiKeys.pageInfo.total),
    }));

  const { hasNextPage, hasPreviousPage, total } = data.users.pageInfo;

  // Every refetch carries the active filter (built from the applied search) so
  // paging never drops the current search; reading the refs keeps this stable.
  const onNavigate = useCallback(
    (variables: CursorVariables) =>
      refetch(
        {
          ...variables,
          filterElement: buildFilter(
            scopeRef.current,
            appliedSearchRef.current,
          ),
        },
        { fetchPolicy: "network-only" },
      ),
    [refetch],
  );

  // Any explicit navigation supersedes a pending debounced search, so cancel it
  // before paging — otherwise its stale callback fires later and resets the
  // page/filter, replacing the navigation just made.
  const onBeforeNavigate = useCallback(
    () => clearTimeout(debounceRef.current),
    [],
  );

  const {
    pageSize,
    isPending,
    rangeStart,
    rangeEnd,
    handleNext,
    handlePrevious,
    handlePageSizeChange,
    goToFirstPage,
  } = useCursorPagination({
    pageInfo: data.users.pageInfo,
    rowCount: rows.length,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    onNavigate,
    onBeforeNavigate,
  });

  // A parent-driven search term or refresh token invalidates the loaded page,
  // so refetch it from the start. The ref (rather than the dependency list
  // alone) decides whether anything actually changed, so a fresh
  // `goToFirstPage` identity after a fetch can't retrigger the refetch that
  // fetch just satisfied.
  const lastRefetchedRef = useRef({ searchTerm, refreshToken });
  useEffect(() => {
    const last = lastRefetchedRef.current;
    if (last.searchTerm === searchTerm && last.refreshToken === refreshToken) {
      return;
    }
    lastRefetchedRef.current = { searchTerm, refreshToken };
    if (searchTerm !== undefined) {
      appliedSearchRef.current = searchTerm;
    }
    goToFirstPage();
  }, [searchTerm, refreshToken, goToFirstPage]);

  const isMemberList =
    scope.kind === "group" && scope.membership !== "non-members";

  // Memoized for a stable identity: DataGridPro re-applies each colDef's own
  // width whenever the `columns` prop reference changes, and XGridTable then
  // re-arms its content autosize — so a fresh array every render makes every
  // re-render visibly bounce the column widths.
  const columns: GridColDef<UserRow>[] = useMemo(() => {
    const cols: GridColDef<UserRow>[] = [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 240,
        renderCell: ({ row }) => (
          <div className="flex h-full items-center gap-sm">
            <Avatar
              email={row.email ?? ""}
              name={row.name ?? row.email ?? ""}
              size={28}
            />
            <div className="leading-tight min-w-0">
              <div className="truncate text-label-medium text-text-primary">
                {row.name ?? "--"}
              </div>
              <div className="truncate text-label-small text-text-secondary">
                {row.email ?? "--"}
              </div>
            </div>
          </div>
        ),
      },
      {
        field: "hasApiKeys",
        headerName: "API Keys",
        width: 120,
        renderCell: ({ row }) =>
          row.hasApiKeys ? (
            <CheckIcon
              aria-label="Has API keys"
              className="size-4 shrink-0 text-text-positive"
            />
          ) : (
            <XIcon
              aria-label="No API keys"
              className="size-4 shrink-0 text-text-secondary"
            />
          ),
      },
      {
        field: "lastLogin",
        headerName: "Last Login",
        type: "dateTime",
        width: 160,
        valueGetter: (params) => (params.value ? new Date(params.value) : null),
        renderCell: ({ row }) => <HumanTimestamp timestamp={row.lastLogin} />,
      },
      {
        field: "actions",
        headerName: "",
        width: 64,
        sortable: false,
        align: "right",
        renderCell: ({ row }) => (
          <Link
            className="inline-flex text-text-secondary hover:text-text-primary"
            textDecoration="none"
            to={`/settings/access-control/users/${row.key}`}
          >
            <ArrowRightIcon
              aria-label={`View ${row.name ?? row.email ?? "user"}`}
              size={16}
            />
          </Link>
        ),
      },
    ];

    if (showSsoOrigin) {
      cols.splice(1, 0, {
        // A group can hold both SSO-provisioned and locally-created users, so
        // the origin needs to be visible per row.
        field: "isSsoUser",
        headerName: "SSO",
        width: 90,
        renderCell: ({ row }) =>
          row.isSsoUser ? (
            <CheckIcon
              aria-label="SSO user"
              className="size-4 shrink-0 text-text-positive"
            />
          ) : (
            <XIcon
              aria-label="Not an SSO user"
              className="size-4 shrink-0 text-text-secondary"
            />
          ),
      });
    }

    if (onTransfer) {
      cols.unshift({
        field: "transfer",
        headerName: "",
        // One icon button, the same on every row, so the column never needs to
        // be any wider than the button. min and max pin it against the grid
        // sizing it from content; FIXED_WIDTH_FIELDS keeps autosize off it too.
        width: TRANSFER_COLUMN_WIDTH,
        minWidth: TRANSFER_COLUMN_WIDTH,
        maxWidth: TRANSFER_COLUMN_WIDTH,
        sortable: false,
        resizable: false,
        hideable: false,
        disableColumnMenu: true,
        align: "center",
        renderCell: ({ row }) => {
          const label = row.name ?? row.email ?? "user";
          return (
            <V2Tooltip
              title={isTransferDisabled ? (transferDisabledReason ?? "") : ""}
              asChild
            >
              <span className="inline-flex">
                <V2Button
                  IconLeft={
                    isMemberList ? (
                      <MinusIcon size={14} />
                    ) : (
                      <PlusIcon size={14} />
                    )
                  }
                  aria-label={
                    isMemberList
                      ? `Remove ${label} from the group`
                      : `Add ${label} to the group`
                  }
                  isDisabled={isTransferDisabled}
                  onPress={() =>
                    onTransfer({ id: row.id, uuid: row.uuid, label })
                  }
                  type="button"
                  variant="plain"
                />
              </span>
            </V2Tooltip>
          );
        },
      });
    }

    return cols;
  }, [
    showSsoOrigin,
    onTransfer,
    isMemberList,
    isTransferDisabled,
    transferDisabledReason,
  ]);

  // Snapshot the export params when the export starts rather than deriving them
  // live from `search`: the export hook re-runs its mutation whenever `params`
  // changes, so a search edit mid-export would otherwise start a second export
  // and retarget the tracked export ID onto a different CSV.
  const handleExport = () => {
    const filter = buildFilter(scope, search);
    setExportParams(
      filter
        ? [
            {
              name: "filterElement",
              type: "FilterElementInput",
              valueJSON: JSON.stringify(filter),
            },
          ]
        : undefined,
    );
    setExportInProgress(true);
  };

  // Debounce the live search, then apply it and reset to the first page. The
  // filter itself is built at navigation time from `appliedSearchRef`.
  const handleSearchChange = (value: string) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      appliedSearchRef.current = value;
      goToFirstPage();
    }, SEARCH_DEBOUNCE_MS);
  };

  const hasHeader = title !== null || description !== null;

  return (
    <div className="flex flex-col">
      {title === null ? null : (
        <div className="border-b border-overlay-medium pb-xs">
          <V2Heading
            className="text-label-medium-strong text-text-primary"
            level={3}
          >
            {title}
          </V2Heading>
        </div>
      )}
      {description === null ? null : (
        <p className="mt-sm text-label-small text-text-secondary">
          {description}
        </p>
      )}

      {enableExport ? (
        <ExportCsv
          columns={CSV_COLUMNS}
          exportInProgress={exportInProgress}
          field="users"
          markComplete={() => setExportInProgress(false)}
          nodeId={null}
          params={exportParams}
          rowCount={total ?? rows.length}
        />
      ) : null}

      <div className={hasHeader ? "mt-md" : ""}>
        <StackletGrid
          className={GRID_CLASS_NAMES}
          columnHeaderHeight={36}
          columns={columns}
          defaultDensity="comfortable"
          exportInProgress={exportInProgress}
          fixedWidthFields={fixedColumnWidths ? ALL_FIELDS : FIXED_WIDTH_FIELDS}
          getRowClassName={() => ""}
          getRowId={(row) => row.id}
          handleExport={enableExport ? handleExport : undefined}
          hasToolbar={!isSearchControlled}
          loading={isPending}
          onSearchChange={isSearchControlled ? undefined : handleSearchChange}
          pagination={{
            pageSize,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            onPageSizeChange: handlePageSizeChange,
            hasNextPage,
            hasPreviousPage,
            onNextPage: handleNext,
            onPreviousPage: handlePrevious,
            rangeStart,
            rangeEnd,
            total,
            unit: "user",
            isDisabled: isPending,
          }}
          rowHeight={52}
          rows={rows}
          search={search}
          searchAriaLabel="Search users"
          tableId={tableId}
          hideDensityButton
        />
      </div>
    </div>
  );
}
